import { useMutation } from '@apollo/client';
import React from 'react';
import { match } from 'ts-pattern';

import { useApiClient } from '@eluve/api-client-provider';
import { cacheUtils, useCompleteFragment } from '@eluve/apollo-client';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Icon,
  SimpleTooltip,
  Text,
  useToast,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';

import {
  GlobalBundleBlocker,
  GlobalBundleBlockerTooltip,
} from './GlobalBundleBlockerTooltip';
import { parseDeactivateErrorMessage } from './parseDeactivateErrorMessage';

export const modelArgsSettingsFragment = graphql(`
  fragment ModelArgs on LlmModelArgs @_unmask {
    __typename
    isGlobalDefault
    isActive
  }
`);

const setModelArgsActive = graphql(`
  mutation setModelArgsActive($id: uuid!) {
    updateLlmModelArgsByPk(pkColumns: { id: $id }, _set: { isActive: true }) {
      __typename
      id
      isActive
    }
  }
`);

export interface ToggleModelActiveActionProps {
  modelArgsId: string;
  /** Enabled tenant bundles still depending on this model, from the list page. */
  blockers?: GlobalBundleBlocker[];
}

export const ToggleModelActiveAction: React.FC<
  ToggleModelActiveActionProps
> = ({ modelArgsId, blockers = [] }) => {
  const toast = useToast();
  const modelArgsSettings = useCompleteFragment({
    fragment: modelArgsSettingsFragment,
    key: {
      id: modelArgsId,
    },
    strict: false,
  });

  const [markModelArgsActive] = useMutation(setModelArgsActive, {
    optimisticResponse: (data) => ({
      updateLlmModelArgsByPk: {
        __typename: 'LlmModelArgs' as const,
        id: data.id,
        isActive: true,
      },
    }),
    onError: () => {
      toast.error('Failed to update model args');
    },
  });

  const apiClient = useApiClient();

  const deactivateModel = async () => {
    const result = await apiClient.prompts.deactivateModelArgs({
      body: {
        modelArgsId,
      },
    });

    if (result.status !== 201) {
      // Surface the server's reason (e.g. "active template bundle(s) depend on
      // it") so the admin knows to disable those bundles first, rather than a
      // generic failure. parseDeactivateErrorMessage strips the exception-class
      // prefix the API filter adds and falls back to a generic message.
      toast.error(parseDeactivateErrorMessage(result.body));
      return;
    }

    cacheUtils.updateFragment(
      {
        fragment: modelArgsSettingsFragment,
        key: {
          id: modelArgsId,
        },
      },
      () => {
        return {
          __typename: 'LlmModelArgs' as const,
          isActive: false,
          isGlobalDefault: modelArgsSettings!.isGlobalDefault,
        };
      },
    );

    toast.success('Successfully deactivated model args');
  };

  const toggleActive = async () => {
    if (modelArgsSettings!.isActive) {
      await deactivateModel();
    } else {
      await markModelArgsActive({
        variables: {
          id: modelArgsId,
        },
      });
    }
  };

  if (!modelArgsSettings || modelArgsSettings.isGlobalDefault) {
    return null;
  }

  const isBlocked = modelArgsSettings.isActive && blockers.length > 0;

  const { label, description, action, Component } = match(
    modelArgsSettings.isActive,
  )
    .with(true, () => {
      const description = (
        <div>
          <Text>
            Marking this model as inactive will make it unavailable to be used
            with prompts
          </Text>
        </div>
      );

      return {
        label: 'Mark this model args as inactive',
        description,
        action: 'Deactivate',
        Component: (
          <Icon
            name="PackageMinus"
            size="xl"
            className="text-bg-orange-strong"
          />
        ),
      };
    })
    .otherwise(() => ({
      label: 'Mark this model as active',
      description: (
        <Text>
          Marking this model as active will make it available to be used with
          prompts
        </Text>
      ),
      action: 'Activate',
      Component: (
        <Icon
          name="PackagePlus"
          size="xl"
          className="text-bg-lightgreen-strong"
        />
      ),
    }));

  // An active model still has active bundles depending on it: disable the
  // toggle up front and list them. The API rejects the deactivation on any
  // active bundle regardless of tenant enablement, so — unlike the prompt /
  // output-template toggles — disabling a bundle per organization does NOT
  // clear the block; the bundle must be deactivated. The instruction wording
  // reflects that.
  if (isBlocked) {
    return (
      <GlobalBundleBlockerTooltip
        blockers={blockers}
        label={label}
        instruction="Deactivate these active bundles before deactivating this model:"
      >
        {Component}
      </GlobalBundleBlockerTooltip>
    );
  }

  return (
    <AlertDialog>
      <SimpleTooltip label={label}>
        <AlertDialogTrigger asChild>
          <Button variant="outline" aria-label={label}>
            {Component}
          </Button>
        </AlertDialogTrigger>
      </SimpleTooltip>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure?</AlertDialogTitle>
          <AlertDialogDescription asChild>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel autoFocus>Cancel</AlertDialogCancel>
          <AlertDialogAction variant="destructive" onClick={toggleActive}>
            {action}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
